import { useEffect, useState, useCallback } from 'react';
import { api } from '../services/api';

interface DNSZone {
  id: string;
  name: string;
  providerId: string;
  providerName: string;
  providerType?: string;
  type: string;
  recordCount?: number;
  status?: 'active' | 'inactive' | 'error' | 'pending';
  lastSync?: string;
  lastModified?: string;
  nameservers?: string[];
  visibility?: 'public' | 'private';
  dnssecEnabled?: boolean;
}

interface DNSRecord {
  id: string;
  name: string;
  type: string;
  value: string;
  ttl: number;
}

export const useZonesData = () => {
  const [zones, setZones] = useState<DNSZone[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoneRecords, setZoneRecords] = useState<DNSRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordsError, setRecordsError] = useState<string | null>(null);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load providers first
      const providersResponse = await api.get('/api/v1/dns/providers');
      const providersData = providersResponse.data?.providers || [];
      setProviders(providersData);

      // Create provider lookup map
      const providerMap = new Map();
      providersData.forEach((provider: any) => {
        providerMap.set(provider.id, {
          name: provider.name,
          type: provider.type,
        });
      });

      // Load zones
      const zonesResponse = await api.get('/api/v1/dns/zones?include=dnssec,email');
      const zonesData = zonesResponse.data?.zones || [];

      // Enhance zones with provider information
      const enhancedZones = zonesData.map((zone: any) => {
        const providerId = zone.provider_id || zone.providerId;
        const providerInfo = providerMap.get(providerId);
        return {
          id: zone.id,
          name: zone.name,
          providerId,
          providerName: providerInfo?.name || 'Unknown Provider',
          providerType: providerInfo?.type || 'unknown',
          type: zone.type || 'primary',
          recordCount: zone.record_count ?? zone.recordCount ?? 0,
          status: zone.status || 'active',
          visibility: zone.visibility || 'public',
          dnssecEnabled: zone.dnssec_enabled ?? zone.dnssecEnabled ?? undefined,
          lastSync: zone.lastSync,
          lastModified: zone.updated_at || zone.updatedAt || zone.lastSync,
          nameservers: zone.nameservers || [],
        } as DNSZone;
      });

      setZones(enhancedZones);
      await fetchRecordCounts(enhancedZones);
    } catch (error: any) {
      console.error('Failed to load data:', error);
      setError('Failed to load DNS zones. Please check your connection and try again.');
      setZones([]);
      setProviders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecordCounts = async (zonesToUpdate: DNSZone[]) => {
    const targetZones = zonesToUpdate.slice(0, 5);

    setZones((prev) =>
      prev.map((z) =>
        targetZones.find((t) => t.id === z.id)
          ? { ...z, recordCountLoading: true, recordCountError: undefined }
          : z,
      ),
    );

    const promises = targetZones.map(async (zone) => {
      try {
        const response = await api.get(
          `/api/v1/dns/records/providers/${zone.providerId}/zones/${zone.id}`,
        );
        const records = response.data?.records || [];
        return { zoneId: zone.id, count: records.length, error: null };
      } catch (error: any) {
        console.warn(`Failed to fetch record count for zone ${zone.id}:`, error);
        const msg = error?.response?.data?.error || error?.message || 'Failed';
        return { zoneId: zone.id, count: 0, error: msg };
      }
    });

    try {
      const results = await Promise.all(promises);
      setZones((prevZones) =>
        prevZones.map((zone) => {
          const result = results.find((r) => r.zoneId === zone.id);
          return result
            ? {
                ...zone,
                recordCount: result.count,
                recordCountLoading: false,
                recordCountError: result.error || undefined,
              }
            : zone;
        }),
      );
    } catch (error) {
      console.warn('Failed to fetch some record counts:', error);
      setZones((prev) => prev.map((z) => ({ ...z, recordCountLoading: false })));
    }
  };

  const loadZoneRecords = async (zoneId: string) => {
    try {
      setRecordsLoading(true);
      setRecordsError(null);
      const zone = zones.find((z) => z.id === zoneId);
      const response = await api.get(
        `/api/v1/dns/records/providers/${zone?.providerId}/zones/${zoneId}`,
      );
      const records = response.data?.records || [];
      setZoneRecords(records);

      setZones((prevZones) =>
        prevZones.map((zone) =>
          zone.id === zoneId ? { ...zone, recordCount: records.length } : zone,
        ),
      );
    } catch (error: any) {
      console.error('Failed to load zone records:', error);
      setRecordsError(error?.response?.data?.error || error?.message || 'Failed to load records');
      setZoneRecords([]);
    } finally {
      setRecordsLoading(false);
    }
  };

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh]);

  return {
    zones,
    setZones,
    providers,
    loading,
    error,
    zoneRecords,
    setZoneRecords,
    recordsLoading,
    recordsError,
    handleRefresh,
    loadZoneRecords,
  };
};